import { useMemo } from "react";
import { useBnbSelector } from "../../../redux/store";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { isLexus } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { ImageOptionsType } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { VERSION_6HD_INT_GALLERY } from "../../../../../../common-deprecated/constants";
import { getCarImageFromConfig } from "../../../../../shared-logic/utils/typedUtils";

export enum ImageType {
    Exterior,
    InteriorGallery,
    Interior3D,
    Top,
}

/**
 * This hook is used to get the required function and cache key for the useSpinner hook in the build and buy car spinners.
 */
export type GetBuildSpinnerFrameType = {
    getImage: (width: number, height: number, frame: number, additionalImageOptions?: ImageOptionsType) => string;
    cacheKey: string;
};
const useGetBuildSpinnerFrame = (
    showWinterWheel: boolean,
    type: ImageType = ImageType.Exterior,
): GetBuildSpinnerFrameType => {
    const basket = useBnbSelector((state) => state.basket);
    const car = useBnbSelector((state) => state.car);
    const items = useBnbSelector((state) => state.items);
    const settings = useBnbSelector((state) => state.commonSettings);

    const isLexusSetting = isLexus(settings);

    const { colour, upholstery, wheel, packs } = basket;
    const extras = CarDataConsolidator.getAllRelevantBasketExtras(basket, items);

    let mode = "day-exterior";
    switch (type) {
        case ImageType.InteriorGallery:
            mode =
                isLexusSetting && car.model.configVersion !== VERSION_6HD_INT_GALLERY
                    ? "day-interior"
                    : "static-day-interior";
            break;
        case ImageType.Interior3D:
            mode = "interior";
            break;
        case ImageType.Top:
            mode = "top-exterior";
            break;
    }

    const packIds = packs.map((pack) => pack.id).join(",");
    const extraIds = extras.map((extra) => extra.id).join(",");
    const winterWheelId = `${String(showWinterWheel)}-${basket.winterWheel?.id as string}`;
    const cacheKey = `${car.model.id}-${car.id}-${colour.id}-${upholstery.id}-${wheel.id}-${winterWheelId}-${packIds}-${extraIds}-${mode}`;

    // Wrap in useMemo so we only return a new function when there is an actual new config.
    return useMemo(
        () => ({
            getImage: (
                width: number,
                height: number,
                frame: number,
                additionalImageOptions?: ImageOptionsType,
            ): string =>
                getCarImageFromConfig(
                    settings,
                    mode,
                    frame,
                    car.model.id,
                    car.id,
                    {
                        width,
                        height,
                        imageQuality: 75,
                        scaleMode: 1,
                        padding: type === ImageType.Exterior ? 10 : 0,
                        bgColour: isLexusSetting ? "F7F7F7" : "F0F0F0",
                        ...additionalImageOptions,
                    },
                    colour,
                    upholstery,
                    showWinterWheel ? basket.winterWheel : wheel,
                    packs,
                    extras,
                    false,
                    showWinterWheel ? basket.winterWheel : null,
                ),
            cacheKey,
        }),
        [cacheKey],
    );
};
export default useGetBuildSpinnerFrame;
